import * as React from "react";
import { Route, Redirect, RouteProps } from "react-router-dom";
import { LoginPage } from "Components/Auth/Login/LoginPage";
import { DashboardPage } from "Components/Dashboard/DashboardPage";
import { AccessRolesPage } from "Components/Settings/AccessRoles/AccessRolesPage";
import { AccessRoleDetailPage } from "Components/Settings/AccessRoles/AccessRoleDetailPage";
import { useResource, Resources } from "Translations/Resources";
import { AuthenticatedRoute } from "Routes/AuthenticatedRoute";
import { AuthorizedRoute } from "Routes/AuthorizedRoute";
import { ApiCalls } from "Routes/ApiCalls";
import { useSelector } from "react-redux";
import { RootStateType } from "State/Store";
import { UsersPage } from "Components/Settings/Users/UsersPage";
import { UserDetailPage } from "Components/Settings/Users/UserDetailPage";
import { Error404Page } from "Components/Error/Error404Page";
import { Error403Page } from "Components/Error/Error403Page";
import { GoogleRedirectPage } from "Components/Auth/Google/GoogleRedirectPage";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { EmployeeEditPage } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditPage";
import { EmployeeDetailPage } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailPage";
import { AccessRightCodes } from "Components/Models/AccessRights";
import { CompaniesPage } from "Components/Companies/CompaniesPage";
import { CompanyEditPage } from "Components/Companies/CompanyEdit/CompanyEditPage";
import { CompanyContactsPage } from "Components/Companies/CompanyContacts/CompanyContactsPage";
import { CompanyDetailPage } from "Components/Companies/CompanyDetail/CompanyDetailPage";
import { EmployeesPage } from "Components/HumanResources/Employees/EmployeesList/EmployeesPage";
import { CompanyContactPersonsChargesHistoryPage } from "Components/Companies/CompanyContactPersonsChargesHistory/CompanyContactPersonsChargesHistoryPage";
import { EmployeeTypeCategoryID } from "Api/Api";
import { InvoiceItemCategoriesPage } from "Components/Settings/InvoiceItemCategories/InvoiceItemCategoriesPage";
import { InvoiceItemCategoryDetailPage } from "Components/Settings/InvoiceItemCategories/InvoiceItemCategoryDetailPage";

const Routes: React.FunctionComponent<RouteProps> = _ => {
  const { t } = useResource();
  const login = `/${t(Resources.Routes.Login)}`;
  const dashboard = `/${t(Resources.Routes.Dashboard)}`;
  const users = `/${t(Resources.Routes.Users)}`;
  const userDetail = `/${t(Resources.Routes.Users)}/${t(
    Resources.Routes.UserDetail,
  )}/:id`;
  const companies = `/${t(Resources.Routes.Companies)}`;
  const companyDetail = `/${t(Resources.Routes.Companies)}/${t(
    Resources.Routes.CompanyDetail,
  )}/:id`;
  const companyEdit = `/${t(Resources.Routes.Companies)}/${t(
    Resources.Routes.CompanyEdit,
  )}/:id`;
  const companyContacts = `/${t(
    Resources.Routes.Companies,
  )}/kontaktni-osoby/:id`;
  const companyContactPersonsChargesHistory = `/${t(
    Resources.Routes.Companies,
  )}/hisorie-sazeb/:id`;
  const companyCreate = `/${t(Resources.Routes.Companies)}/${t(
    Resources.Routes.CompanyCreate,
  )}`;
  const accessRoles = `/${t(Resources.Routes.AccessRoles)}`;
  const accessRoleDetail = `/${t(Resources.Routes.AccessRoles)}/${t(
    Resources.Routes.AccessRoleDetail,
  )}/:id`;

  const invoiceItemCategories = `/${t(Resources.Routes.InvoiceItemCategories)}`;
  const invoiceItemCategoryDetail = `/${t(
    Resources.Routes.InvoiceItemCategories,
  )}/${t(Resources.Routes.InvoiceItemCategoryDetail)}/:code`;
  const invoiceItemCategoryCreate = `/${t(
    Resources.Routes.InvoiceItemCategories,
  )}/${t(Resources.Routes.InvoiceItemCategoryCreate)}`;

  const error403 = `/${t(Resources.Routes.Error403)}`;
  const error404 = `/${t(Resources.Routes.Error404)}`;

  const user = useSelector((e: RootStateType) => e.auth.user);

  const getEmployeeSection = (
    baseUrl: string,
    employeeTypeCategoryID: EmployeeTypeCategoryID,
  ) => {
    const readRight =
      employeeTypeCategoryID === EmployeeTypeCategoryID.Internal
        ? AccessRightCodes.Core_HumanResources_Employee_Read
        : AccessRightCodes.Core_HumanResources_ExternalSuppliers_Read;

    const editRight =
      employeeTypeCategoryID === EmployeeTypeCategoryID.Internal
        ? AccessRightCodes.Core_HumanResources_Employee_Edit
        : AccessRightCodes.Core_HumanResources_ExternalSuppliers_Edit;

    return [
      <AuthenticatedRoute
        key={1}
        user={user}
        path={`/${baseUrl}/${t(Resources.Routes.EmployeeDetail)}/:id`}
      >
        <AuthorizedRoute codes={[readRight]}>
          <EmployeeDetailPage employeeTypeCategoryID={employeeTypeCategoryID} />
        </AuthorizedRoute>
      </AuthenticatedRoute>,
      <AuthenticatedRoute
        user={user}
        key={2}
        path={`/${baseUrl}/${t(Resources.Routes.EmployeeEdit)}/:id`}
      >
        <AuthorizedRoute codes={[editRight]}>
          <EmployeeEditPage
            isCreate={false}
            employeeTypeCategoryID={employeeTypeCategoryID}
          />
        </AuthorizedRoute>
      </AuthenticatedRoute>,
      <AuthenticatedRoute
        user={user}
        key={3}
        path={`/${baseUrl}/${t(Resources.Routes.EmployeeCreate)}`}
      >
        <AuthorizedRoute codes={[editRight]}>
          <EmployeeEditPage
            isCreate={true}
            employeeTypeCategoryID={employeeTypeCategoryID}
          />
        </AuthorizedRoute>
      </AuthenticatedRoute>,
    ];
  };

  return (
    <Router>
      <ApiCalls error403={error403} error404={error404}></ApiCalls>
      <Switch>
        <Route path="/" exact={true}>
          <Redirect to={dashboard} />
        </Route>
        <Route path={login}>
          <LoginPage />
        </Route>
        <Route path="/auth/google">
          <GoogleRedirectPage />
        </Route>
        {getEmployeeSection(
          t(Resources.Routes.Employees),
          EmployeeTypeCategoryID.Internal,
        )}
        <AuthenticatedRoute
          exact={true}
          user={user}
          path={`/${t(Resources.Routes.Employees)}`}
        >
          <AuthorizedRoute
            codes={[AccessRightCodes.Core_HumanResources_Employee_Read]}
          >
            <EmployeesPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute user={user} path={dashboard}>
          <DashboardPage />
        </AuthenticatedRoute>

        <AuthenticatedRoute exact user={user} path={users}>
          <AuthorizedRoute codes={[AccessRightCodes.Core_Users_Read]}>
            <UsersPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute exact user={user} path={userDetail}>
          <AuthorizedRoute codes={[AccessRightCodes.Core_Users_Edit]}>
            <UserDetailPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>

        <AuthenticatedRoute exact user={user} path={accessRoles}>
          <AuthorizedRoute codes={[AccessRightCodes.Core_AccessRoles_Read]}>
            <AccessRolesPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute exact user={user} path={accessRoleDetail}>
          <AuthorizedRoute codes={[AccessRightCodes.Core_AccessRoles_Edit]}>
            <AccessRoleDetailPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute exact user={user} path={companies}>
          <AuthorizedRoute
            codes={[
              AccessRightCodes.Core_Company_Customer_Read,
              AccessRightCodes.Core_Company_Supplier_Read,
            ]}
          >
            <CompaniesPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute user={user} path={companyEdit}>
          <AuthorizedRoute
            codes={[
              AccessRightCodes.Core_Company_Customer_Edit,
              AccessRightCodes.Core_Company_Supplier_Edit,
            ]}
          >
            <CompanyEditPage isCreate={false} companiesListUrl={companies} />
          </AuthorizedRoute>
        </AuthenticatedRoute>

        <AuthenticatedRoute
          user={user}
          path={companyContactPersonsChargesHistory}
        >
          <AuthorizedRoute
            codes={[
              AccessRightCodes.Core_Company_Customer_Read,
              AccessRightCodes.Core_Company_Supplier_Read,
            ]}
          >
            <CompanyContactPersonsChargesHistoryPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>

        <AuthenticatedRoute user={user} path={companyContacts}>
          <AuthorizedRoute
            codes={[
              AccessRightCodes.Core_Company_Customer_Read,
              AccessRightCodes.Core_Company_Supplier_Read,
            ]}
          >
            <CompanyContactsPage companiesListUrl={companies} />
          </AuthorizedRoute>
        </AuthenticatedRoute>

        <AuthenticatedRoute user={user} path={companyCreate}>
          <AuthorizedRoute
            codes={[
              AccessRightCodes.Core_Company_Customer_Edit,
              AccessRightCodes.Core_Company_Supplier_Edit,
            ]}
          >
            <CompanyEditPage isCreate={true} companiesListUrl={companies} />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute user={user} path={companyDetail}>
          <AuthorizedRoute
            codes={[
              AccessRightCodes.Core_Company_Customer_Read,
              AccessRightCodes.Core_Company_Supplier_Read,
            ]}
          >
            <CompanyDetailPage companiesListUrl={companies} />
          </AuthorizedRoute>
        </AuthenticatedRoute>

        <AuthenticatedRoute exact user={user} path={invoiceItemCategories}>
          <AuthorizedRoute
            codes={[AccessRightCodes.Core_InvoiceItemCategories_Read]}
          >
            <InvoiceItemCategoriesPage />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute exact user={user} path={invoiceItemCategoryCreate}>
          <AuthorizedRoute
            codes={[AccessRightCodes.Core_InvoiceItemCategories_Edit]}
          >
            <InvoiceItemCategoryDetailPage isCreate={true} />
          </AuthorizedRoute>
        </AuthenticatedRoute>
        <AuthenticatedRoute exact user={user} path={invoiceItemCategoryDetail}>
          <AuthorizedRoute
            codes={[AccessRightCodes.Core_InvoiceItemCategories_Edit]}
          >
            <InvoiceItemCategoryDetailPage isCreate={false} />
          </AuthorizedRoute>
        </AuthenticatedRoute>

        <Route path={error403} exact={true}>
          <Error403Page />
        </Route>

        <Route>
          <Error404Page />
        </Route>
      </Switch>
    </Router>
  );
};

export { Routes };
